/** Financial settings — the account classification screen.
 *
 * A trial balance says an account is an "Asset". It does not say whether it is
 * cash or inventory, and the ERP's own classification columns are empty on
 * every account of every tenant. Without that distinction there is no working
 * capital, no current ratio and no liquidity answer — so rather than guess, we
 * draft a classification and ask.
 *
 * The server decides what is still missing and whether financial answers may
 * render. This screen renders that decision; it never computes its own, because
 * a screen that says "all good" while the gate disagrees is worse than no
 * screen.
 */
import {
  AlertTriangle,
  Check,
  ChevronDown,
  ChevronRight,
  Info,
  Loader2,
  Pencil,
  Search,
  ShieldCheck,
  X
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  ChartAccount,
  LINE_GROUP,
  LINE_LABEL,
  MapChange,
  Question,
  Ruling,
  FinancialSettings as Settings,
  StandardLine,
  apiApproveMap,
  apiFinancialSettings,
  apiSaveRulings,
  formatMoney
} from '@/components/financial/api';
import {
  headingCodes,
  isApprovedNow,
  pausedOnlyForApproval
} from '@/components/financial/view';
import { Header } from '@/components/header';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { SidebarProvider } from '@/components/ui/sidebar';

/** A pending edit the user has made but not saved. Keyed account|scope. */
type Pending = Record<string, Ruling>;

const key = (code: string, scope: 'subtree' | 'account') => `${code}|${scope}`;

export default function FinancialSettings() {
  const [data, setData] = useState<Settings | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [pending, setPending] = useState<Pending>({});
  const [search, setSearch] = useState('');
  const [showAll, setShowAll] = useState(false);
  const [year, setYear] = useState<number | null>(null);
  // A signed-off classification is shown to read, not as a form. Editing is
  // something a person chooses to do, not the screen's resting state.
  const [editing, setEditing] = useState(false);
  const [checksOpen, setChecksOpen] = useState(false);

  // Who is signing is decided by the server from the signed-in identity, not
  // sent from here - it ends up on a financial sign-off, so it is not the
  // client's to claim.

  const load = useCallback(async (fy?: number | null) => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiFinancialSettings(fy ?? undefined);
      setData(res);
      setYear(res.financial_year ?? null);
      setPending({});
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load settings.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const setLine = (
    code: string,
    scope: 'subtree' | 'account',
    line: StandardLine
  ) =>
    setPending((p) => ({
      ...p,
      [key(code, scope)]: { account_code: code, scope, standard_line: line }
    }));

  const save = async () => {
    const rulings = Object.values(pending);
    if (!rulings.length) return;
    setSaving(true);
    setError(null);
    try {
      await apiSaveRulings(rulings);
      setEditing(false);
      await load(year);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save.');
    } finally {
      setSaving(false);
    }
  };

  const cancel = () => {
    setPending({});
    setEditing(false);
  };

  const approve = async () => {
    setSaving(true);
    setError(null);
    try {
      await apiApproveMap();
      await load(year);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not approve.');
    } finally {
      setSaving(false);
    }
  };

  const rulingFor = useMemo(() => {
    const m = new Map<string, Ruling>();
    data?.rulings.forEach((r) => m.set(key(r.account_code, r.scope), r));
    return m;
  }, [data]);

  const accounts = useMemo(() => {
    if (!data) return [];
    const q = search.trim().toLowerCase();
    let rows = data.accounts;
    if (!showAll) {
      // Level-2 nodes are where classification actually happens; everything
      // below inherits. Showing 730 rows by default buries that.
      rows = rows.filter((a) => a.depth != null && a.depth <= 2);
    }
    if (q) {
      rows = data.accounts.filter(
        (a) =>
          a.code.toLowerCase().includes(q) ||
          (a.name || '').toLowerCase().includes(q) ||
          (a.name_alt || '').toLowerCase().includes(q)
      );
    }
    return rows;
  }, [data, search, showAll]);

  const pendingCount = Object.keys(pending).length;
  const blocking = data?.checks.filter(
    (c) => !c.passed && c.id !== 'map_approved'
  );
  const approvedNow = isApprovedNow(data);
  const pausedForApproval = pausedOnlyForApproval(data);
  const readOnly = approvedNow && !editing;
  const headings = useMemo(() => headingCodes(data?.accounts ?? []), [data]);
  const accountByCode = useMemo(() => {
    const m = new Map<string, ChartAccount>();
    data?.accounts.forEach((a) => m.set(a.code, a));
    return m;
  }, [data]);
  const changes = data?.changes_since_approval ?? [];
  const passedCount = data?.checks.filter((c) => c.passed).length ?? 0;
  const questions = (data?.questions ?? []).filter(
    // The paused card carries the approve button; a second one below it
    // would be the same action twice.
    (q) => !(pausedForApproval && q.kind === 'needs_approval')
  );

  if (loading) {
    return (
      <Shell>
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading the chart of accounts…
        </div>
      </Shell>
    );
  }

  if (data && !data.available) {
    return (
      <Shell>
        <Banner tone="warn" icon={<AlertTriangle className="h-4 w-4" />}>
          {data.reason}
        </Banner>
      </Shell>
    );
  }

  return (
    <Shell>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between sm:gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            Financial settings
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Which accounts make up each line of your financial statements.
            Figures come from the {data?.basis}.
          </p>
        </div>
        {data && data.financial_years.length > 1 ? (
          <select
            className="h-9 rounded-md border bg-background px-2 text-sm"
            value={year ?? ''}
            onChange={(e) => {
              const v = Number(e.target.value);
              setYear(v);
              void load(v);
            }}
          >
            {data.financial_years.map((y) => (
              <option key={y} value={y}>
                Financial year {y}
              </option>
            ))}
          </select>
        ) : null}
      </div>

      {error ? (
        <Banner tone="warn" icon={<X className="h-4 w-4" />}>
          {error}
        </Banner>
      ) : null}

      {data?.drafted ? (
        <Banner tone="info" icon={<Info className="h-4 w-4" />}>
          Nothing has been saved for this tenant yet. What you see below is a
          draft worked out from the account names — it is a proposal, not an
          answer. Correct anything that is wrong, then approve it.
        </Banner>
      ) : null}

      {data && !data.prior_years_reliable ? (
        <Banner tone="info" icon={<Info className="h-4 w-4" />}>
          Figures for closed financial years are not shown yet. Year-end closing
          entries were not separated in the data loaded so far, and including
          them makes a closed year&apos;s profit collapse towards zero. This
          clears once the journal summary has been loaded again.
        </Banner>
      ) : null}

      {/* ── Status ─────────────────────────────────────────────────────── */}
      {data?.ok ? (
        <section className="rounded-xl border border-emerald-300 bg-emerald-50 p-4 text-emerald-900">
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-emerald-700" />
            <h2 className="text-base font-semibold">
              Financial answers are switched on
            </h2>
          </div>
          <p className="text-sm mt-1">
            {data.approval
              ? `Approved by ${data.approval.approved_by} on ${signedOn(data.approval.approved_at)}. Nothing needs your attention.`
              : 'Every check passed, so financial questions can be answered for this company.'}
          </p>
          <button
            type="button"
            className="mt-2 inline-flex items-center gap-1 text-sm font-medium"
            onClick={() => setChecksOpen((v) => !v)}
          >
            {passedCount} of {data.checks.length} checks passed
            {checksOpen ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
          </button>
          {checksOpen ? <CheckList checks={data.checks} /> : null}
        </section>
      ) : pausedForApproval ? (
        <section className="rounded-xl border border-amber-300 bg-amber-50 p-4 text-amber-900">
          <div className="flex flex-wrap items-start gap-3">
            <div className="flex-1 min-w-[12rem]">
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-amber-700" />
                <h2 className="text-base font-semibold">
                  Financial answers are paused
                </h2>
              </div>
              <p className="text-sm mt-1">
                {changes.length
                  ? `${changes.length} classification${
                      changes.length === 1 ? '' : 's'
                    } changed since the last approval. Review, then approve.`
                  : 'The classification changed since the last approval. Review, then approve.'}
              </p>
            </div>
            <Button
              onClick={approve}
              disabled={!!pendingCount || saving}
              title={pendingCount ? 'Save your changes first.' : undefined}
            >
              Approve classification
            </Button>
          </div>
          {changes.length ? (
            <ul className="mt-3 divide-y divide-amber-200 rounded-lg border border-amber-200 bg-white/60">
              {changes.map((c) => (
                <ChangeRow
                  key={`${c.account_code}|${c.scope}`}
                  c={c}
                  a={accountByCode.get(c.account_code)}
                />
              ))}
            </ul>
          ) : null}
        </section>
      ) : (
        <section
          className="rounded-xl border p-4"
          style={{ background: 'var(--sig-surface)' }}
        >
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-600" />
            <h2 className="text-base font-semibold">
              Financial answers are switched off
            </h2>
          </div>
          <p className="text-sm text-muted-foreground mt-1">
            Signals will not answer financial questions for this company until
            every check below passes. It will not show an approximate number
            instead.
          </p>
          {data ? <CheckList checks={data.checks} /> : null}
        </section>
      )}

      {/* ── Questions ──────────────────────────────────────────────────── */}
      {data && questions.length ? (
        <section className="space-y-3">
          <h2 className="text-base font-semibold">
            Needs an answer ({questions.length})
          </h2>
          {questions.map((q, i) => (
            <QuestionRow
              key={`${q.kind}-${q.account_code ?? i}`}
              q={q}
              lines={data.standard_lines}
              current={
                q.account_code
                  ? (pending[key(q.account_code, 'account')]?.standard_line ??
                    data.accounts.find((a) => a.code === q.account_code)
                      ?.line ??
                    null)
                  : null
              }
              onPick={(line) =>
                q.account_code && setLine(q.account_code, 'account', line)
              }
              onApprove={q.kind === 'needs_approval' ? approve : undefined}
              approveDisabled={!!blocking?.length || saving}
              approveReason={
                blocking?.length
                  ? 'Answer the questions above first.'
                  : undefined
              }
            />
          ))}
        </section>
      ) : null}

      {/* ── The chart ──────────────────────────────────────────────────── */}
      <section className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="text-base font-semibold">Accounts</h2>
          <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">
            {readOnly ? (
              <Button
                variant="outline"
                size="sm"
                className="hidden sm:inline-flex"
                onClick={() => setEditing(true)}
              >
                <Pencil className="h-4 w-4 mr-1" />
                Edit classification
              </Button>
            ) : null}
            <div className="relative basis-full sm:basis-auto">
              <Search className="h-4 w-4 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                className="h-9 pl-8 w-full sm:w-56"
                placeholder="Search code or name"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowAll((v) => !v)}
            >
              {showAll ? (
                <ChevronDown className="h-4 w-4 mr-1" />
              ) : (
                <ChevronRight className="h-4 w-4 mr-1" />
              )}
              {showAll ? 'Main groups only' : 'Show every account'}
            </Button>
          </div>
        </div>

        <p className="text-sm text-muted-foreground">
          {readOnly
            ? 'This is the classification financial answers use.'
            : 'Classifying a group covers every account beneath it. Change an individual account only where it does not belong with its group.'}
        </p>

        <div className="hidden sm:block rounded-xl border overflow-hidden">
          <table className="w-full text-sm">
            <thead style={{ background: 'var(--sig-tab-active)' }}>
              <tr className="text-left">
                <th className="px-3 py-2 font-medium">Code</th>
                <th className="px-3 py-2 font-medium">Account</th>
                <th className="px-3 py-2 font-medium">In the ERP</th>
                <th className="px-3 py-2 font-medium text-right">Balance</th>
                <th className="px-3 py-2 font-medium">Belongs to</th>
              </tr>
            </thead>
            <tbody>
              {accounts.map((a) => (
                <AccountRow
                  key={a.code}
                  a={a}
                  readOnly={readOnly}
                  heading={headings.has(a.code)}
                  lines={data?.standard_lines ?? []}
                  pending={
                    pending[key(a.code, 'subtree')] ??
                    pending[key(a.code, 'account')]
                  }
                  ruled={
                    rulingFor.get(key(a.code, 'account')) ??
                    rulingFor.get(key(a.code, 'subtree'))
                  }
                  onPick={(line) =>
                    setLine(a.code, a.is_leaf ? 'account' : 'subtree', line)
                  }
                />
              ))}
              {!accounts.length ? (
                <tr>
                  <td
                    colSpan={5}
                    className="px-3 py-6 text-center text-muted-foreground"
                  >
                    No accounts match that search.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>

        <ul className="sm:hidden rounded-xl border divide-y">
          {accounts.map((a) => (
            <AccountItem
              key={a.code}
              a={a}
              readOnly={readOnly}
              heading={headings.has(a.code)}
              lines={data?.standard_lines ?? []}
              pending={
                pending[key(a.code, 'subtree')] ??
                pending[key(a.code, 'account')]
              }
              ruled={
                rulingFor.get(key(a.code, 'account')) ??
                rulingFor.get(key(a.code, 'subtree'))
              }
              onPick={(line) =>
                setLine(a.code, a.is_leaf ? 'account' : 'subtree', line)
              }
            />
          ))}
          {!accounts.length ? (
            <li className="px-3 py-6 text-center text-sm text-muted-foreground">
              No accounts match that search.
            </li>
          ) : null}
        </ul>
      </section>

      {/* ── Footer ─────────────────────────────────────────────────────── */}
      <div
        className="sticky bottom-0 flex items-center justify-between gap-3 rounded-xl border p-3"
        style={{ background: 'var(--sig-surface)' }}
      >
        <div className="text-sm text-muted-foreground">
          {readOnly
            ? 'Read only. Edit to change a classification.'
            : pendingCount
              ? `${pendingCount} change${pendingCount === 1 ? '' : 's'} not saved`
              : data?.approval
                ? `${approvedNow ? 'Approved' : 'Last approved'} by ${data.approval.approved_by} on ${signedOn(data.approval.approved_at)}.`
                : 'Not approved yet.'}
        </div>
        <div className="flex items-center gap-2">
          {readOnly ? (
            <Button
              variant="outline"
              className="sm:hidden"
              onClick={() => setEditing(true)}
            >
              <Pencil className="h-4 w-4 mr-1" />
              Edit
            </Button>
          ) : pendingCount || editing ? (
            // Nothing to save, nothing to cancel: no buttons. A greyed-out
            // Save on a screen that only needs an approval reads as a task.
            <>
              <Button variant="outline" disabled={saving} onClick={cancel}>
                Cancel
              </Button>
              <Button disabled={!pendingCount || saving} onClick={save}>
                {saving ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-1" />
                ) : null}
                Save changes
              </Button>
            </>
          ) : null}
        </div>
      </div>
    </Shell>
  );
}

/** "12 Oct 2026": a sign-off date that cannot be read as 10 December. */
function signedOn(at: string) {
  return new Date(at).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <SidebarProvider>
      <div className="flex flex-col h-screen w-full">
        <Header />
        <div className="flex-1 overflow-auto">
          <div className="max-w-5xl mx-auto px-6 py-6 space-y-6">
            {children}
          </div>
        </div>
      </div>
    </SidebarProvider>
  );
}

function Banner({
  tone,
  icon,
  children
}: {
  tone: 'info' | 'warn';
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div
      className={`flex gap-2 rounded-xl border p-3 text-sm ${
        tone === 'warn'
          ? 'border-amber-300 bg-amber-50 text-amber-900'
          : 'border-sky-200 bg-sky-50 text-sky-900'
      }`}
    >
      <span className="mt-0.5 shrink-0">{icon}</span>
      <span>{children}</span>
    </div>
  );
}

function LinePicker({
  value,
  lines,
  onPick
}: {
  value: StandardLine | null;
  lines: StandardLine[];
  onPick: (line: StandardLine) => void;
}) {
  const grouped = useMemo(() => {
    const g: Record<string, StandardLine[]> = {};
    lines.forEach((l) => {
      (g[LINE_GROUP[l]] ||= []).push(l);
    });
    return g;
  }, [lines]);

  return (
    <select
      className="h-8 w-full rounded-md border bg-background px-2 text-sm"
      value={value ?? ''}
      onChange={(e) => onPick(e.target.value as StandardLine)}
    >
      <option value="" disabled>
        Not classified
      </option>
      {Object.entries(grouped).map(([group, ls]) => (
        <optgroup key={group} label={group}>
          {ls.map((l) => (
            <option key={l} value={l}>
              {LINE_LABEL[l]}
            </option>
          ))}
        </optgroup>
      ))}
    </select>
  );
}

/** What a row shows where the picker would be: the line, or why there is none. */
function LineCell({
  a,
  readOnly,
  heading,
  lines,
  pending,
  ruled,
  onPick
}: {
  a: ChartAccount;
  readOnly: boolean;
  heading: boolean;
  lines: StandardLine[];
  pending?: Ruling;
  ruled?: Ruling;
  onPick: (line: StandardLine) => void;
}) {
  if (heading) {
    return <span className="text-muted-foreground">Heading</span>;
  }
  const current = pending?.standard_line ?? a.line ?? null;
  if (readOnly) {
    return current ? (
      <span>{LINE_LABEL[current]}</span>
    ) : (
      <span className="text-muted-foreground">Not classified</span>
    );
  }
  const isDraft = !pending && ruled?.source === 'auto';
  return (
    <>
      <LinePicker value={current} lines={lines} onPick={onPick} />
      {pending ? (
        <div className="text-[11px] text-sky-700 mt-0.5">
          Changed, not saved
        </div>
      ) : isDraft ? (
        <div className="text-[11px] text-muted-foreground mt-0.5">
          Suggested — not confirmed
        </div>
      ) : null}
    </>
  );
}

type RowProps = {
  a: ChartAccount;
  readOnly: boolean;
  heading: boolean;
  lines: StandardLine[];
  pending?: Ruling;
  ruled?: Ruling;
  onPick: (line: StandardLine) => void;
};

function AccountRow(props: RowProps) {
  const { a, heading } = props;
  return (
    <tr
      className={`border-t align-middle ${heading ? 'text-muted-foreground' : ''}`}
      style={heading ? { background: 'var(--sig-surface)' } : undefined}
    >
      <td className="px-3 py-2 font-mono text-[12px] whitespace-nowrap">
        {a.code}
      </td>
      <td className="px-3 py-2">
        <div style={{ paddingLeft: Math.max(0, (a.depth ?? 1) - 1) * 12 }}>
          {a.name}
          {a.name_alt ? (
            <span className="text-muted-foreground"> · {a.name_alt}</span>
          ) : null}
        </div>
      </td>
      <td className="px-3 py-2 text-muted-foreground whitespace-nowrap">
        {a.group}
      </td>
      <td className="px-3 py-2 text-right font-mono text-[12px] whitespace-nowrap">
        {a.balance ? formatMoney(a.balance) : '—'}
      </td>
      <td className="px-3 py-2 w-64">
        <LineCell {...props} />
      </td>
    </tr>
  );
}

/** The same account on a phone: two lines, the picker full width beneath. */
function AccountItem(props: RowProps) {
  const { a, heading, readOnly } = props;
  return (
    <li
      className="px-3 py-2 text-sm"
      style={heading ? { background: 'var(--sig-surface)' } : undefined}
    >
      <div className="flex items-start justify-between gap-2">
        <span className={heading ? 'text-muted-foreground' : ''}>
          {a.name}
          {a.name_alt ? (
            <span className="text-muted-foreground"> · {a.name_alt}</span>
          ) : null}
        </span>
        <span className="font-mono text-[11px] text-muted-foreground whitespace-nowrap">
          {a.code}
        </span>
      </div>
      {readOnly || heading ? (
        <div className="mt-0.5 flex justify-between gap-2 text-[12px] text-muted-foreground">
          <LineCell {...props} />
          <span className="font-mono whitespace-nowrap">
            {a.balance ? formatMoney(a.balance) : '—'}
          </span>
        </div>
      ) : (
        <div className="mt-1">
          <div className="text-right font-mono text-[11px] text-muted-foreground">
            {a.balance ? formatMoney(a.balance) : '—'}
          </div>
          <LineCell {...props} />
        </div>
      )}
    </li>
  );
}

function CheckList({ checks }: { checks: Settings['checks'] }) {
  return (
    <ul className="mt-3 space-y-2">
      {checks.map((c) => (
        <li key={c.id} className="flex gap-2 text-sm">
          {c.passed ? (
            <Check className="h-4 w-4 mt-0.5 shrink-0 text-emerald-600" />
          ) : (
            <X className="h-4 w-4 mt-0.5 shrink-0 text-amber-600" />
          )}
          <span>
            <span className="font-medium">{c.label}</span>
            <span className="opacity-80"> — {c.detail}</span>
          </span>
        </li>
      ))}
    </ul>
  );
}

/** One account that moved since the sign-off: the old line struck through. */
function ChangeRow({ c, a }: { c: MapChange; a?: ChartAccount }) {
  const label = (l: StandardLine | null) =>
    l ? LINE_LABEL[l] : 'Not classified';
  return (
    <li className="px-3 py-2 text-sm">
      <div className="flex items-start justify-between gap-2">
        <span>
          {a?.name ?? c.account_code}
          {a?.name_alt ? (
            <span className="opacity-70"> · {a.name_alt}</span>
          ) : null}
        </span>
        <span className="font-mono text-[11px] opacity-70">
          {c.account_code}
        </span>
      </div>
      <div className="mt-0.5 text-[12px]">
        <span className="line-through opacity-70">{label(c.old_line)}</span>
        {' → '}
        <span className="font-medium">{label(c.new_line)}</span>
        {c.changed_by ? (
          <span className="opacity-70"> · by {c.changed_by}</span>
        ) : null}
      </div>
    </li>
  );
}

function QuestionRow({
  q,
  lines,
  current,
  onPick,
  onApprove,
  approveDisabled,
  approveReason
}: {
  q: Question;
  lines: StandardLine[];
  current: StandardLine | null;
  onPick: (line: StandardLine) => void;
  onApprove?: () => void;
  approveDisabled?: boolean;
  approveReason?: string;
}) {
  const isLedger = q.kind === 'ledger_imbalance';
  return (
    <div
      className={`rounded-xl border p-3 ${
        isLedger ? 'border-amber-300 bg-amber-50' : ''
      }`}
      style={isLedger ? undefined : { background: 'var(--sig-surface)' }}
    >
      <div className="flex items-start gap-2">
        {isLedger ? (
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0 text-amber-700" />
        ) : (
          <Info className="h-4 w-4 mt-0.5 shrink-0 text-muted-foreground" />
        )}
        <div className="flex-1">
          <p className={`text-sm ${isLedger ? 'text-amber-900' : ''}`}>
            {q.text}
          </p>
          {q.account_code ? (
            <div className="mt-2 flex items-center gap-3">
              <span className="font-mono text-[12px] text-muted-foreground">
                {q.account_code}
              </span>
              {q.balance ? (
                <span className="font-mono text-[12px] text-muted-foreground">
                  {formatMoney(q.balance)}
                </span>
              ) : null}
              <div className="w-64">
                <LinePicker value={current} lines={lines} onPick={onPick} />
              </div>
            </div>
          ) : null}
          {onApprove ? (
            <div className="mt-2 flex items-center gap-2">
              <Button
                size="sm"
                onClick={onApprove}
                disabled={approveDisabled}
                title={approveReason}
              >
                Approve classification
              </Button>
              {approveReason ? (
                <span className="text-xs text-muted-foreground">
                  {approveReason}
                </span>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
